import React, { useRef, useEffect, useState } from "react";
import Globe from "react-globe.gl";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";

export default function WorldGlobe({ expeditions }) {
  const { L, t } = useI18n();
  const nav = useNavigate();
  const containerRef = useRef(null);
  const globeRef = useRef(null);
  const [dims, setDims] = useState({ w: 800, h: 500 });
  const [ready, setReady] = useState(false);
  const [active, setActive] = useState(null);
  const isTouch = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        setDims({ w, h: Math.min(Math.max(w * 0.7, 360), 600) });
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  useEffect(() => {
    if (globeRef.current) {
      const controls = globeRef.current.controls();
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.45;
      controls.enableZoom = true;
      controls.minDistance = 180;
      controls.maxDistance = 500;
      globeRef.current.pointOfView({ lat: 22, lng: 10, altitude: 2.3 }, 0);
      setReady(true);
    }
  }, [dims.w]);

  const handleClick = (d) => {
    if (isTouch) {
      if (active && active.slug === d.slug) nav(`/expeditions/${d.slug}`);
      else setActive(d);
    } else {
      nav(`/expeditions/${d.slug}`);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full flex justify-center overflow-hidden" data-testid="world-globe">
      {/* Destination reveal (hover desktop / tap mobile) */}
      {active && (
        <div data-testid="globe-active-label" className="absolute top-3 left-1/2 -translate-x-1/2 z-10 bg-[#141414] border border-[#BC5D2E]/60 px-4 py-2 text-center">
          <div className="font-clash uppercase tracking-tight text-sm">{L(active.title)}</div>
          <div className="font-mono-hs text-[10px] text-[#A3A3A3] uppercase tracking-widest">{L(active.location)}</div>
          {isTouch && (
            <button onClick={() => nav(`/expeditions/${active.slug}`)} className="mt-2 font-mono-hs text-[10px] uppercase tracking-widest text-[#BC5D2E]">{t("globe_view")} →</button>
          )}
        </div>
      )}
      <Globe
        ref={globeRef}
        width={dims.w}
        height={dims.h}
        backgroundColor="rgba(0,0,0,0)"
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-dark.jpg"
        bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
        showAtmosphere={true}
        atmosphereColor="#BC5D2E"
        atmosphereAltitude={0.16}
        pointsData={expeditions}
        pointLat="lat"
        pointLng="lng"
        pointColor={() => "#BC5D2E"}
        pointAltitude={0.07}
        pointRadius={0.55}
        pointResolution={16}
        pointLabel={() => ""}
        onPointHover={(d) => { if (!isTouch) setActive(d || null); }}
        onPointClick={handleClick}
        ringsData={expeditions}
        ringLat="lat"
        ringLng="lng"
        ringColor={() => (tick) => `rgba(188,93,46,${1 - tick})`}
        ringMaxRadius={4}
        ringPropagationSpeed={2}
        ringRepeatPeriod={1400}
      />
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center font-mono-hs text-[#BC5D2E] text-xs uppercase tracking-widest animate-pulse">// ...</div>
      )}
    </div>
  );
}
